import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Radar, Skull, ExternalLink, RefreshCw, Loader2, AlertTriangle,
  KeyRound, ShieldAlert, FileText, Fingerprint,
} from "lucide-react";
import { cn } from "@/lib/util";
import { SocialIntelSection } from "@/components/security/SocialIntelSection";
import { UnifiedLeaksSection } from "@/components/security/UnifiedLeaksSection";

const RISK_TONE = {
  high:   "border-sev-critical/60 text-sev-critical bg-sev-critical/10",
  medium: "border-sev-high/60 text-sev-high bg-sev-high/10",
  low:    "border-sev-low/60 text-sev-low bg-sev-low/10",
};

const TYPE_ICON = {
  paste: FileText, "paste-user": FileText, forum: FileText, leak: ShieldAlert,
  mention: Fingerprint, onion_mention: Skull, tweet: Fingerprint,
  pulse: Radar,
};

const fmtDate = (v) => {
  if (!v) return "—";
  try {
    const d = new Date(v);
    return isNaN(d) ? String(v) : d.toLocaleString();
  } catch { return String(v); }
};

export default function ThreatIntel() {
  const [iocs, setIocs] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [dw, setDw] = useState({ intelx: [], ahmia: [], otx: [], errors: [], last_scan_at: null, selectors: [] });
  const [dwStatus, setDwStatus] = useState({ intelx_configured: false, ahmia_configured: true, otx_configured: false });
  const [scanning, setScanning] = useState(false);
  const [loadingDw, setLoadingDw] = useState(false);

  useEffect(() => { api.get("/threat-intelligence/iocs").then(r => setIocs(r.data)); }, []);
  useEffect(() => {
    api.get("/clients").then(r => {
      setClients(r.data || []);
      if (r.data?.length) setClientId(r.data[0].id);
    });
    api.get("/darkweb/status").then(r => setDwStatus(r.data)).catch(() => {});
  }, []);

  const loadDw = async (cid) => {
    if (!cid) return;
    setLoadingDw(true);
    try {
      const r = await api.get(`/darkweb/results/${cid}`);
      setDw(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar Dark Web");
    } finally { setLoadingDw(false); }
  };
  useEffect(() => { if (clientId) loadDw(clientId); /* eslint-disable-next-line */ }, [clientId]);

  const runScan = async () => {
    if (!clientId) return;
    setScanning(true);
    try {
      const r = await api.post(`/darkweb/scan/${clientId}`);
      setDw({ ...r.data, last_scan_at: r.data.last_scan_at });
      const total = (r.data.intelx?.length || 0) + (r.data.ahmia?.length || 0) + (r.data.otx?.length || 0);
      toast.success(`Escaneo completo · ${total} hallazgo${total === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo escanear");
    } finally { setScanning(false); }
  };

  const grouped = iocs.reduce((acc, i) => { (acc[i.kind] ||= []).push(i); return acc; }, {});
  const currentClient = useMemo(() => clients.find(c => c.id === clientId), [clients, clientId]);

  return (
    <div className="space-y-8 animate-fade-in" data-testid="threat-intel-page">
      <header>
        <div className="overline mb-2">CTI</div>
        <h1 className="font-display text-4xl font-medium tracking-tight">Inteligencia de amenazas</h1>
        <p className="text-muted-foreground text-sm mt-1">Indicadores de compromiso, exposición en dark web y correlación con tus activos.</p>
      </header>

      {/* ============ Dark Web Exposure ============ */}
      <section className="border border-border bg-card/60" data-testid="darkweb-section">
        <div className="p-5 border-b border-border flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Skull className="w-4 h-4 text-sev-critical" />
            <h2 className="font-display text-lg font-medium">Exposición en Dark Web</h2>
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground ml-2">
              Intelligence X + Ahmia (Tor)
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={clientId} onChange={e => setClientId(e.target.value)}
                    className="bg-background border border-border px-3 py-1.5 text-xs font-mono focus:border-primary outline-none"
                    data-testid="darkweb-client-selector">
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <button onClick={() => loadDw(clientId)} disabled={!clientId || loadingDw}
                    className="border border-border p-1.5 hover:border-primary hover:text-primary disabled:opacity-30"
                    title="Recargar último snapshot"
                    data-testid="darkweb-reload-btn">
              <RefreshCw className={cn("w-3 h-3", loadingDw && "animate-spin")} />
            </button>
            <button onClick={runScan} disabled={!clientId || scanning}
                    className="bg-primary text-primary-foreground px-3 py-1.5 font-mono text-[10px] tracking-widest uppercase inline-flex items-center gap-2 hover:bg-primary/90 disabled:opacity-50"
                    data-testid="darkweb-scan-btn">
              {scanning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Radar className="w-3 h-3" />}
              {scanning ? "Escaneando…" : "Escanear ahora"}
            </button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Meta bar */}
          <div className="flex flex-wrap gap-4 text-[11px] font-mono text-muted-foreground">
            <div>
              <span className="uppercase tracking-widest">Último escaneo:</span>{" "}
              <span className="text-foreground" data-testid="darkweb-last-scan">
                {fmtDate(dw.last_scan_at)}
              </span>
            </div>
            <div>
              <span className="uppercase tracking-widest">Selectores:</span>{" "}
              <span className="text-foreground">{dw.selectors?.length || 0}</span>
              {dw.selectors?.length > 0 && (
                <span className="ml-1 text-muted-foreground/70">
                  ({dw.selectors.slice(0, 3).join(", ")}{dw.selectors.length > 3 ? "…" : ""})
                </span>
              )}
            </div>
            <div className="inline-flex items-center gap-1">
              <KeyRound className="w-3 h-3" />
              IntelX: <span className={cn("uppercase tracking-widest",
                dwStatus.intelx_configured ? "text-sev-low" : "text-sev-high")}>
                {dwStatus.intelx_configured ? "configurado" : "no configurado"}
              </span>
            </div>
            <div className="inline-flex items-center gap-1">
              <KeyRound className="w-3 h-3" />
              OTX: <span className={cn("uppercase tracking-widest",
                dwStatus.otx_configured ? "text-sev-low" : "text-sev-high")}>
                {dwStatus.otx_configured ? "configurado" : "no configurado"}
              </span>
            </div>
          </div>

          {/* Warnings / errors */}
          {!dwStatus.intelx_configured && (
            <div className="border border-sev-high/40 bg-sev-high/5 p-3 flex items-start gap-2 text-xs"
                 data-testid="darkweb-intelx-warning">
              <AlertTriangle className="w-4 h-4 text-sev-high mt-0.5 shrink-0" />
              <div>
                Falta la variable de entorno <code className="font-mono text-sev-high">INTELX_API_KEY</code>.
                Los resultados sólo mostrarán fuentes públicas de Ahmia hasta que se configure.
              </div>
            </div>
          )}
          {!dwStatus.otx_configured && (
            <div className="border border-sev-high/40 bg-sev-high/5 p-3 flex items-start gap-2 text-xs"
                 data-testid="darkweb-otx-warning">
              <AlertTriangle className="w-4 h-4 text-sev-high mt-0.5 shrink-0" />
              <div>
                Falta la variable de entorno <code className="font-mono text-sev-high">OTX_API_KEY</code>.
                Obtén una gratuita en <a href="https://otx.alienvault.com/api" target="_blank" rel="noreferrer noopener" className="underline text-primary">otx.alienvault.com/api</a> para enriquecer los resultados con pulses de AlienVault.
              </div>
            </div>
          )}
          {dw.errors?.length > 0 && (
            <details className="border border-sev-medium/40 bg-sev-medium/5 p-3 text-xs"
                     data-testid="darkweb-errors">
              <summary className="cursor-pointer text-sev-medium font-mono uppercase tracking-widest text-[10px]">
                {dw.errors.length} advertencia{dw.errors.length === 1 ? "" : "s"} durante el escaneo
              </summary>
              <ul className="mt-2 space-y-1 font-mono text-[11px] text-muted-foreground">
                {dw.errors.map((e, i) => (
                  <li key={i}>[{e.source}] {e.message}</li>
                ))}
              </ul>
            </details>
          )}

          {/* Results tables */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <DwCard title="Intelligence X" icon={ShieldAlert} testid="darkweb-intelx-card"
                    subtitle="Pastes · foros · filtraciones · leaks"
                    empty={dwStatus.intelx_configured
                      ? "Sin coincidencias en las últimas ejecuciones."
                      : "Requiere INTELX_API_KEY."}>
              {dw.intelx.map((r, i) => <DwRow key={`ix-${i}`} r={r} />)}
            </DwCard>
            <DwCard title="AlienVault OTX" icon={Radar} testid="darkweb-otx-card"
                    subtitle="Pulses · IOCs · TTPs de la comunidad"
                    empty={dwStatus.otx_configured
                      ? "Sin pulses para los selectores actuales."
                      : "Requiere OTX_API_KEY."}>
              {(dw.otx || []).map((r, i) => <DwRow key={`ox-${i}`} r={r} />)}
            </DwCard>
            <DwCard title="Ahmia (Tor)" icon={Skull} testid="darkweb-ahmia-card"
                    subtitle="Sitios .onion indexados"
                    empty="Sin menciones en el índice público.">
              {dw.ahmia.map((r, i) => <DwRow key={`ah-${i}`} r={r} />)}
            </DwCard>
          </div>
        </div>
      </section>

      {/* ============ Social Media Intelligence ============ */}
      <SocialIntelSection clients={clients} clientId={clientId} onClientChange={setClientId} />

      {/* ============ Unified Leaks (HIBP + IntelX) ============ */}
      <UnifiedLeaksSection clients={clients} clientId={clientId} onClientChange={setClientId} />

      {/* ============ IOC summary (existing) ============ */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {["ip", "domain", "url", "hash"].map(k => (
          <div key={k} className="border border-border bg-card/60 p-4">
            <div className="overline">{k}</div>
            <div className="font-display text-3xl mt-1">{grouped[k]?.length || 0}</div>
          </div>
        ))}
      </div>

      <div className="border border-border bg-card/60">
        <div className="p-5 border-b border-border flex items-center gap-2">
          <Radar className="w-4 h-4 text-primary" />
          <h2 className="font-display text-lg font-medium">Indicadores</h2>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left overline border-b border-border">
              <th className="p-3">Tipo</th>
              <th className="p-3">Valor</th>
              <th className="p-3">Actor</th>
              <th className="p-3">Campaña</th>
              <th className="p-3">Confianza</th>
              <th className="p-3">Fuente</th>
            </tr>
          </thead>
          <tbody>
            {iocs.map(i => (
              <tr key={i.id} data-row-hover className="border-b border-border/50">
                <td className="p-3 font-mono text-[10px] tracking-widest uppercase text-muted-foreground">{i.kind}</td>
                <td className="p-3 font-mono text-xs">{i.value}</td>
                <td className="p-3 text-xs">{i.threat_actor || "—"}</td>
                <td className="p-3 text-xs">{i.campaign || "—"}</td>
                <td className="p-3 font-mono text-xs">{i.confidence}%</td>
                <td className="p-3 font-mono text-[11px] text-muted-foreground">{i.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DwCard({ title, icon: Icon, testid, subtitle, empty, children }) {
  const empty_ = !children || (Array.isArray(children) && children.length === 0);
  return (
    <div className="border border-border bg-background/40" data-testid={testid}>
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <Icon className="w-3.5 h-3.5 text-primary" />
        <div>
          <div className="text-sm">{title}</div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">{subtitle}</div>
        </div>
      </div>
      <div className="divide-y divide-border/40">
        {empty_ ? (
          <div className="p-6 text-center text-xs font-mono text-muted-foreground">{empty}</div>
        ) : children}
      </div>
    </div>
  );
}

function DwRow({ r }) {
  const Icon = TYPE_ICON[r.type] || Fingerprint;
  return (
    <div className="px-4 py-3 hover:bg-muted/30" data-testid={`darkweb-row-${r.origin}`}>
      <div className="flex items-center gap-2 justify-between mb-1">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="w-3 h-3 text-muted-foreground shrink-0" />
          <span className="text-sm truncate">{r.title || r.source}</span>
        </div>
        <span className={cn("border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest shrink-0",
                            RISK_TONE[r.risk_level] || RISK_TONE.low)}>
          {r.risk_level}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] text-muted-foreground">
        <span className="uppercase tracking-widest">{r.type}</span>
        <span>·</span>
        <span>{fmtDate(r.date)}</span>
        <span>·</span>
        <span className="text-foreground/80">{r.selector}</span>
        {r.url && (
          <a href={r.url} target="_blank" rel="noreferrer noopener"
             className="text-primary hover:underline inline-flex items-center gap-1 ml-auto">
            {r.origin === "ahmia" ? "abrir .onion" : "abrir"}
            <ExternalLink className="w-2.5 h-2.5" />
          </a>
        )}
      </div>
      {r.snippet && (
        <pre className="mt-2 font-mono text-[10px] text-muted-foreground bg-background/40 border border-border/40 p-2 whitespace-pre-wrap break-words max-h-24 overflow-y-auto">
          {r.snippet}
        </pre>
      )}
    </div>
  );
}
